import { defineComponent, onBeforeUnmount, onMounted, watch } from 'vue';

import {
  getCurrentInstance,
  getHorizontalScrollPosition,
  getScrollTarget,
  getVerticalScrollPosition,
  IN_BROWSER,
  noop
} from '@components/utils';

import type { PropType } from 'vue';

const axisValues = ['both', 'horizontal', 'vertical'] as const;
type TAxis = typeof axisValues[number];

export type TScrollObserver = {
  position: {
    top: number;
    left: number;
  };
  direction: 'up' | 'down' | 'left' | 'right';
  directionChanged: boolean;
  delta: {
    top: number;
    left: number;
  };
  inflectionPoint: {
    top: number;
    left: number;
  };
};

export const LayoutScrollObserver = defineComponent({
  name: 'CdsLayoutScrollObserver',
  props: {
    axis: {
      type: String as PropType<TAxis>,
      validator: (value: TAxis) => axisValues.includes(value)
    },
    debounce: {
      type: [String, Number] as PropType<string | number>,
      default: undefined
    },
    scrollTarget: {
      type: [String, Object] as PropType<string | HTMLElement>,
      default: undefined
    }
  },
  emits: {
    scroll: (data: TScrollObserver) => true
  },
  setup(props, { emit, expose }) {
    if (!IN_BROWSER) return noop;

    const scroll: TScrollObserver = {
      position: {
        top: 0,
        left: 0
      },
      direction: 'down',
      directionChanged: false,
      delta: {
        top: 0,
        left: 0
      },
      inflectionPoint: {
        top: 0,
        left: 0
      }
    };

    let clearTimer: any | null = null;
    let localScrollTarget: HTMLElement | undefined;
    let parentEl: HTMLElement | undefined;

    const emitEvent = () => {
      clearTimer !== null && clearTimer();

      const top = Math.max(0, getVerticalScrollPosition(localScrollTarget));
      const left = getHorizontalScrollPosition(localScrollTarget);

      const delta = {
        top: top - scroll.position.top,
        left: left - scroll.position.left
      };

      if ((props.axis === 'vertical' && delta.top === 0) || (props.axis === 'horizontal' && delta.left === 0)) return;

      const currentDir =
        Math.abs(delta.top) >= Math.abs(delta.left)
          ? delta.top < 0
            ? 'up'
            : 'down'
          : delta.left < 0
          ? 'left'
          : 'right';

      scroll.position = { top, left };
      scroll.directionChanged = scroll.direction !== currentDir;
      scroll.delta = delta;

      if (scroll.directionChanged) {
        scroll.direction = currentDir;
        scroll.inflectionPoint = scroll.position;
      }

      emit('scroll', scroll);
    };

    const configureScrollTarget = () => {
      localScrollTarget = getScrollTarget(parentEl, props.scrollTarget);
      localScrollTarget?.addEventListener('scroll', () => trigger(true), { passive: false });
      trigger(true);
    };

    const unConfigureScrollTarget = () => {
      if (localScrollTarget !== undefined) {
        localScrollTarget.removeEventListener('scroll', () => trigger(true));
        localScrollTarget = undefined;
      }
    };

    const trigger = (immediately?: boolean) => {
      if (immediately || props.debounce === 0 || props.debounce === '0') {
        emitEvent();
      } else if (clearTimer === null) {
        const [timer, fn] = props.debounce
          ? [setTimeout(emitEvent, Number(props.debounce), clearTimeout)]
          : [requestAnimationFrame, cancelAnimationFrame];

        clearTimer = () => {
          fn?.(timer as number);
          clearTimer = null;
        };
      }
    };

    const { proxy } = getCurrentInstance('scroll-observer');

    onMounted(() => {
      parentEl = proxy?.$el?.parentNode;
      configureScrollTarget();
    });

    onBeforeUnmount(() => {
      clearTimer !== null && clearTimer();
      unConfigureScrollTarget();
    });

    watch(
      () => props.scrollTarget,
      () => {
        unConfigureScrollTarget();
        configureScrollTarget();
      }
    );

    expose({
      proxy,
      trigger,
      getPosition: () => scroll
    });

    return noop;
  }
});
